import React, { useRef } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useCurrentProfile, useOrganizationAlias } from "@domain/currentProfile/hooks";
import { Profile } from "@domain/user/profile";
import { PaginatedDataResult, useIsMobile } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { desktop, mobile } from "@ui/styles/utils";
import { Secondary } from "@ui/components/containers/textBlocks";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Stack } from "@ui/components/layout";
import { FollowButton } from "./followButton";
import { UserView } from "./userView";

interface ProfileListModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	data: PaginatedDataResult<Profile>;
	title: string;
	emptyMessage: string;
}
export const ProfileListModal: React.FC<ProfileListModalProps> = ({
	title,
	emptyMessage,
	data,
	close,
	...htmlProps
}) => {
	const { result: users, totalElements, loading, moreLoading, fetchMore } = data;
	const currentProfile = useCurrentProfile();
	const alias = useOrganizationAlias();
	const isMobile = useIsMobile();
	const contentRef = useRef<HTMLDivElement>(null);

	return !loading ? (
		<StyledModal title={title} {...htmlProps} close={close}>
			<Content ref={contentRef}>
				{!totalElements && !loading ? (
					<Secondary>{emptyMessage}</Secondary>
				) : (
					<InfiniteScroll onEndReached={fetchMore} scrollingElement={contentRef} loading={loading || moreLoading}>
						<Stack gap={10}>
							{users.map((profile) =>
								profile && profile.id ? (
									<Row key={profile.id}>
										<BigUserView
											key={profile.id}
											user={profile}
											showDescription
											pictureSize={"M"}
											onClick={close}
											renderBottomAccessory={
												isMobile && currentProfile?.id !== profile.id && !alias
													? () => <StyledFollowButton profile={profile} />
													: undefined
											}
										/>
										{!isMobile && currentProfile?.id !== profile.id && !alias && <FollowButton profile={profile} />}
									</Row>
								) : undefined
							)}
						</Stack>
					</InfiniteScroll>
				)}
				{(loading || moreLoading) && <ClipLoader />}
			</Content>
		</StyledModal>
	) : (
		<ClipLoader />
	);
};

const StyledModal = styled(ClosableModal)`
	padding: 20px 0 0;

	.modal-title {
		margin-left: 20px;
	}
`;

const Content = styled.div`
	padding: 20px;
	border-top: 1px solid ${({ theme }) => theme.colors.lightergray};
	margin-top: 20px;
	max-height: 370px;
	overflow: auto;
`;

const BigUserView = styled(UserView)`
	${({ theme }) => theme.textStyles.primary};
	font-weight: bold;
	flex-shrink: 1;

	.profile-picture {
		margin-right: 20px;
	}

	@media ${desktop} {
		margin-right: 30px;
	}
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;

	> button {
		flex-shrink: 0;
	}
`;

const StyledFollowButton = styled(FollowButton)`
	@media ${mobile} {
		margin-top: 10px;
	}
`;
